import React from 'react';

/**
 * Shared mini area chart SVG component used across Models and Charts modules.
 * Renders a gradient-filled line chart with configurable color and height.
 *
 * @param {Object} props
 * @param {number[]} props.data - Array of numeric values to plot
 * @param {string|number} props.uniqueId - Unique identifier for SVG gradient defs
 * @param {number} [props.height=120] - Chart height in SVG units
 * @param {string} [props.color='#8B5CF6'] - Stroke and gradient color
 * @param {number} [props.strokeWidth=0.5] - Line stroke width
 * @param {string} [props.className='material-chart'] - CSS class for the SVG element
 */
const MiniChartSVG = ({
  data,
  uniqueId,
  height = 120,
  color = '#8B5CF6',
  strokeWidth = 0.5,
  className = 'material-chart',
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const paddingTop = 10;
  const paddingBottom = 10;
  const paddingLeft = 0;
  const paddingRight = 0;

  const chartWidth = 100 - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const linePoints = data
    .map((value, index) => {
      const x = paddingLeft + (index / (data.length - 1)) * chartWidth;
      const y = paddingTop + (chartHeight - ((value - min) / range) * chartHeight);
      return `${x},${y}`;
    })
    .join(' ');

  const areaPoints = `${paddingLeft},${height - paddingBottom} ${linePoints} ${chartWidth},${height - paddingBottom}`;

  const gradientId = `gradient-mini-${uniqueId}`;

  return (
    <svg
      width="100%"
      height={height}
      className={className}
      preserveAspectRatio="none"
      viewBox={`0 0 100 ${height}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.5 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${gradientId})`} points={areaPoints} />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        points={linePoints}
      />
    </svg>
  );
};

export default MiniChartSVG;
